// Offline-First Data Manager
// Primary data source is IndexedDB, Supabase is sync target
// All app data flows through this manager

import { User } from '@supabase/supabase-js';
import { AltzeroTable, getOfflineDB } from '../utils/offlineDB';
import { supabase } from '../utils/supabaseClient';

export interface DataResult<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  fromCache: boolean;
  needsSync?: boolean;
}

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

export interface OfflineChange {
  id: string;
  table: string;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  data: any;
  timestamp: number;
  userId: string;
}

export class OfflineFirstDataManager {
  private static instance: OfflineFirstDataManager;
  private userId: string | null = null;
  private syncInProgress = false;
  private syncQueue: OfflineChange[] = [];
  private permissionErrorCount: number = 0;
  private lastPermissionErrorTime: number | null = null;
  private readonly VIRTUAL_TABLES = ['_cached_invitations', '_sync_queue', '_offline_metadata'];

  static getInstance(): OfflineFirstDataManager {
    if (!OfflineFirstDataManager.instance) {
      OfflineFirstDataManager.instance = new OfflineFirstDataManager();
    }
    return OfflineFirstDataManager.instance;
  }

  /**
   * Initialize for a specific user
   */
  async initialize(user: User): Promise<void> {
    this.userId = user.id;
    console.log('[OfflineFirstDataManager] Initialized for user:', user.email);

    // Perform initial data sync if online
    if (navigator.onLine) {
      await this.performInitialSync();
    }

    // Set up periodic sync
    this.setupPeriodicSync();
  }

  /**
   * Get data from IndexedDB (primary source)
   */
  async getData<T>(table: AltzeroTable, id?: string): Promise<DataResult<T | T[]>> {
    try {
      if (!this.userId) {
        throw new Error('Data manager not initialized');
      }

      const offlineDB = getOfflineDB(this.userId);

      let data: any;
      if (id) {
        data = await offlineDB.getById(table, id);
      } else {
        data = await offlineDB.getAll(table);
      }

      // Check if we have pending changes for this data
      const needsSync = await this.hasPendingChanges(table, id);

      return {
        success: true,
        data,
        fromCache: true,
        needsSync
      };
    } catch (error) {
      console.error('[OfflineFirstDataManager] Failed to get data:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get data',
        fromCache: true
      };
    }
  }

  /**
   * Save data to IndexedDB (primary storage)
   */
  async saveData<T>(
    table: AltzeroTable,
    data: T,
    operation: 'INSERT' | 'UPDATE' = 'UPDATE'
  ): Promise<DataResult<T>> {
    try {
      if (!this.userId) {
        throw new Error('Data manager not initialized');
      }

      const offlineDB = getOfflineDB(this.userId);

      // Add metadata
      const enrichedData = {
        ...data,
        updated_at: new Date().toISOString(),
        _offline_modified: true,
        _sync_pending: true
      };

      // Save to IndexedDB
      await this.storeInIndexedDB(table, enrichedData);

      // Queue for sync
      await this.queueForSync(table, enrichedData, operation);

      // Attempt immediate sync if online
      if (navigator.onLine) {
        this.triggerBackgroundSync();
      }

      return {
        success: true,
        data: enrichedData,
        fromCache: true,
        needsSync: true
      };
    } catch (error) {
      console.error('[OfflineFirstDataManager] Failed to save data:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to save data',
        fromCache: true
      };
    }
  }

  /**
   * Get user profile (specialized method)
   */
  async getUserProfile(): Promise<DataResult<any>> {
    if (!this.userId) {
      throw new Error('Data manager not initialized');
    }

    return await this.getData('base_profiles', this.userId);
  }

  /**
   * Update user profile (specialized method)
   */
  async updateUserProfile(profileData: any): Promise<DataResult<any>> {
    if (!this.userId) {
      throw new Error('Data manager not initialized');
    }

    const enrichedProfile = {
      id: this.userId,
      ...profileData,
      updated_at: new Date().toISOString()
    };

    return await this.saveData('base_profiles', enrichedProfile, 'UPDATE');
  }

  /**
   * Perform initial data sync from Supabase to IndexedDB
   */
  async performInitialSync(): Promise<void> {
    if (!this.userId || this.syncInProgress) return;

    // CRITICAL: Don't sync if offline - preserve existing cached data
    if (!navigator.onLine) {
      console.log('[OfflineFirstDataManager] Skipping initial sync - offline mode. Using cached data.');
      return;
    }

    try {
      this.syncInProgress = true;
      console.log('[OfflineFirstDataManager] Starting initial sync...');

      const offlineDB = getOfflineDB(this.userId);

      // Sync essential tables first - only tables user has access to
      const essentialTables: AltzeroTable[] = [
        'base_profiles',  // User can only access their own profile
        'base_organisation_members'  // User can only access their own memberships
        // Removed base_user_roles, base_groups, base_tenants as they require organization membership
      ];

      // Also sync invitations if online
      await this.syncInvitations();

      for (const table of essentialTables) {
        try {
          await this.syncTableFromSupabase(table);
          console.log(`[OfflineFirstDataManager] Synced ${table}`);
        } catch (error) {
          console.error(`[OfflineFirstDataManager] Failed to sync ${table}:`, error);
          // If we get a 403 error, it means the user doesn't have access to this table
          // This is expected for some tables due to RLS policies
          if (error.code === '42501' || error.message?.includes('permission denied')) {
            // Don't stop the whole sync, just log it
            console.warn(`[OfflineFirstDataManager] Permission denied for ${table}`);
          }
        }
      }
    } catch (error) {
      console.error('[OfflineFirstDataManager] Initial sync failed:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Sync a specific table from Supabase to IndexedDB
   */
  private async syncTableFromSupabase(table: AltzeroTable): Promise<void> {
    try {
      // CRITICAL: Don't sync if offline - preserve existing cached data
      if (!navigator.onLine) {
        console.log(`[OfflineFirstDataManager] Skipping sync for ${table} - offline mode. Preserving cached data.`);
        return;
      }

      // Get data from Supabase
      // For base_profiles, only sync the current user's profile due to RLS policies
      let query = supabase.from(table).select('*');

      // Get authenticated user for filtering
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log(`[OfflineFirstDataManager] No authenticated user for ${table} sync`);
        return;
      }

      if (table === 'base_profiles') {
        // Only sync the current user's profile
        query = query.eq('id', user.id);
      } else if (table === 'base_organisation_members') {
        // Only sync the current user's organization memberships
        query = query.eq('user_id', user.id);
      }

      const { data, error } = await query;

      if (error) {
        console.error(`[OfflineFirstDataManager] Supabase error for ${table}:`, error);
        // CRITICAL: Don't clear data on error - preserve existing cache
        return;
      }

      if (!data || data.length === 0) {
        console.log(`[OfflineFirstDataManager] No data found for ${table}`);
        // CRITICAL: Don't clear data if no data returned - preserve existing cache
        return;
      }

      // Store in IndexedDB
      const offlineDB = getOfflineDB(this.userId!);

      // Clear existing data and insert fresh data
      await this.bulkReplaceInIndexedDB(table, data);

      console.log(`[OfflineFirstDataManager] Synced ${data.length} records for ${table}`);
    } catch (error) {
      console.error(`[OfflineFirstDataManager] Failed to sync ${table}:`, error);
      // CRITICAL: Don't throw - preserve existing data
      return;
    }
  }

  /**
   * Store data in IndexedDB
   */
  private async storeInIndexedDB(table: AltzeroTable, data: any): Promise<void> {
    const offlineDB = getOfflineDB(this.userId!);
    await offlineDB.ensureReady();

    return new Promise((resolve, reject) => {
      const transaction = offlineDB.db!.transaction([table], 'readwrite');
      const store = transaction.objectStore(table);

      const request = store.put(data);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Bulk replace data in IndexedDB
   */
  private async bulkReplaceInIndexedDB(table: AltzeroTable, data: any[]): Promise<void> {
    const offlineDB = getOfflineDB(this.userId!);
    await offlineDB.ensureReady();

    return new Promise((resolve, reject) => {
      const transaction = offlineDB.db!.transaction([table], 'readwrite');
      const store = transaction.objectStore(table);

      // CRITICAL: Don't clear if no data to insert - preserve existing cache
      if (data.length === 0) {
        console.warn(`[OfflineFirstDataManager] bulkReplaceInIndexedDB called with empty data for ${table} - skipping clear to preserve existing data`);
        resolve();
        return;
      }

      // Clear existing data
      const clearRequest = store.clear();

      clearRequest.onsuccess = () => {
        // Insert new data
        let completed = 0;
        const total = data.length;

        data.forEach(record => {
          const request = store.add(record);

          request.onsuccess = () => {
            completed++;
            if (completed === total) resolve();
          };

          request.onerror = () => reject(request.error);
        });
      };

      clearRequest.onerror = () => reject(clearRequest.error);
    });
  }

  /**
   * Queue data for sync to Supabase
   */
  private async queueForSync(table: AltzeroTable, data: any, operation: 'INSERT' | 'UPDATE' | 'DELETE'): Promise<void> {
    const change: OfflineChange = {
      id: `${table}_${data.id || Date.now()}`,
      table,
      operation,
      data,
      timestamp: Date.now(),
      userId: this.userId!
    };

    this.syncQueue.push(change);

    // Store in persistent queue
    await this.persistSyncQueue();
  }

  /**
   * Check if there are pending changes for specific data
   */
  private async hasPendingChanges(table: AltzeroTable, id?: string): Promise<boolean> {
    return this.syncQueue.some(change =>
      change.table === table &&
      (!id || change.data.id === id)
    );
  }

  /**
   * Persist sync queue to IndexedDB
   */
  private async persistSyncQueue(): Promise<void> {
    if (!this.userId) return;

    const offlineDB = getOfflineDB(this.userId);
    await offlineDB.ensureReady();

    return new Promise((resolve, reject) => {
      const transaction = offlineDB.db!.transaction(['_pending_mutations'], 'readwrite');
      const store = transaction.objectStore('_pending_mutations');

      // Clear existing queue
      const clearRequest = store.clear();

      clearRequest.onsuccess = () => {
        let completed = 0;
        const total = this.syncQueue.length;

        if (total === 0) {
          resolve();
          return;
        }

        this.syncQueue.forEach(change => {
          const request = store.add(change);

          request.onsuccess = () => {
            completed++;
            if (completed === total) resolve();
          };

          request.onerror = () => reject(request.error);
        });
      };

      clearRequest.onerror = () => reject(clearRequest.error);
    });
  }

  /**
   * Trigger background sync
   */
  private triggerBackgroundSync(): void {
    // Debounce sync calls
    setTimeout(() => {
      if (!this.syncInProgress) {
        this.syncToSupabase();
      }
    }, 1000);
  }

  /**
   * Sync pending changes to Supabase
   */
  private async syncToSupabase(): Promise<SyncResult> {
    if (!navigator.onLine || this.syncInProgress || this.syncQueue.length === 0) {
      return { success: true, synced: 0, failed: 0, errors: [] };
    }

    // Circuit breaker: If we've had too many permission errors recently, don't sync
    const now = Date.now();
    const PERMISSION_ERROR_THRESHOLD = 3;
    const BACKOFF_TIME = 60000; // 1 minute

    if (this.permissionErrorCount >= PERMISSION_ERROR_THRESHOLD &&
      this.lastPermissionErrorTime &&
      (now - this.lastPermissionErrorTime) < BACKOFF_TIME) {
      console.log('[OfflineFirstDataManager] Circuit breaker active due to permission errors, skipping sync');
      return { success: true, synced: 0, failed: 0, errors: [] };
    }

    try {
      this.syncInProgress = true;
      console.log('[OfflineFirstDataManager] Starting sync to Supabase...');

      let synced = 0;
      let failed = 0;
      const errors: string[] = [];

      for (const change of this.syncQueue) {
        try {
          await this.syncSingleChange(change);
          synced++;

          // Reset permission error count on successful sync
          if (this.permissionErrorCount > 0) {
            this.permissionErrorCount = 0;
            this.lastPermissionErrorTime = null;
          }
        } catch (error) {
          failed++;
          errors.push(`${change.table}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      // Clear successfully synced items
      this.syncQueue = this.syncQueue.filter((_, index) => index >= synced);
      await this.persistSyncQueue();

      console.log(`[OfflineFirstDataManager] Sync completed: ${synced} synced, ${failed} failed`);

      return { success: true, synced, failed, errors };
    } catch (error) {
      console.error('[OfflineFirstDataManager] Sync failed:', error);
      return {
        success: false,
        synced: 0,
        failed: this.syncQueue.length,
        errors: [error instanceof Error ? error.message : 'Sync failed']
      };
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Sync a single change to Supabase
   */
  private async syncSingleChange(change: OfflineChange): Promise<void> {
    const { table, operation, data } = change;

    // Skip virtual tables that only exist in IndexedDB
    if (this.VIRTUAL_TABLES.includes(table)) {
      console.log(`[OfflineFirstDataManager] Skipping sync for virtual table: ${table}`);
      return; // Don't sync virtual tables to Supabase
    }

    // Remove offline-specific metadata
    const cleanData = { ...data };
    delete cleanData._offline_modified;
    delete cleanData._sync_pending;

    try {
      switch (operation) {
        case 'INSERT':
          const { error: insertError } = await supabase
            .from(table)
            .insert(cleanData);
          if (insertError) throw insertError;
          break;

        case 'UPDATE':
          const { error: updateError } = await supabase
            .from(table)
            .update(cleanData)
            .eq('id', data.id);
          if (updateError) throw updateError;
          break;

        case 'DELETE':
          const { error: deleteError } = await supabase
            .from(table)
            .delete()
            .eq('id', data.id);
          if (deleteError) throw deleteError;
          break;
      }
    } catch (error) {
      // Check if this is a 403 Forbidden error (RLS/permission issue)
      const is403Error = error?.message?.includes('403') ||
        error?.message?.includes('Forbidden') ||
        error?.message?.includes('permission denied') ||
        error?.code === '42501' ||
        error?.details?.includes('policy');

      if (is403Error) {
        console.warn(`[OfflineFirstDataManager] 403/Permission error for ${table} ${operation}, skipping sync for this item`);

        // Track permission errors for circuit breaker
        this.permissionErrorCount++;
        this.lastPermissionErrorTime = Date.now();

        // For 403 errors, we don't want to keep retrying as it's a permission issue
        // Just log and continue - don't throw to avoid infinite retries
        return;
      } else {
        // For other errors, re-throw to trigger retry logic
        throw error;
      }
    }
  }

  /**
   * Setup periodic sync
   */
  private setupPeriodicSync(): void {
    // Sync every 30 seconds when online
    setInterval(() => {
      if (navigator.onLine && this.syncQueue.length > 0) {
        this.triggerBackgroundSync();
      }
    }, 30000);

    // Listen for online events
    window.addEventListener('online', () => {
      console.log('[OfflineFirstDataManager] Back online - triggering sync');
      this.triggerBackgroundSync();
    });
  }

  /**
   * Get sync status
   */
  getSyncStatus(): { pending: number; syncing: boolean } {
    return {
      pending: this.syncQueue.length,
      syncing: this.syncInProgress
    };
  }

  /**
   * Force sync now
   */
  async forcSync(): Promise<SyncResult> {
    return await this.syncToSupabase();
  }

  /**
   * Sync invitations from Supabase to IndexedDB
   */
  private async syncInvitations(): Promise<void> {
    try {
      console.log('[OfflineFirstDataManager] Syncing invitations...');

      // Import invitation services dynamically to avoid circular dependencies
      const { getUserPendingOrganizationInvitations } = await import('./organizationService');
      const { getUserPendingTeamInvitations } = await import('./teamService');
      const { offlineFirstInvitationService } = await import('./offlineFirstInvitationService');

      // Get invitations from Supabase
      const [orgInvitations, teamInvitations] = await Promise.all([
        getUserPendingOrganizationInvitations(),
        getUserPendingTeamInvitations(),
      ]);

      // Cache them in IndexedDB
      await offlineFirstInvitationService.cacheInvitations(
        orgInvitations || [],
        teamInvitations || []
      );

      console.log('[OfflineFirstDataManager] Invitations synced successfully');
    } catch (error) {
      console.error('[OfflineFirstDataManager] Failed to sync invitations:', error);
      // Don't throw - invitation sync failure shouldn't break the whole sync
    }
  }
}

// Export singleton instance
export const offlineFirstDataManager = OfflineFirstDataManager.getInstance();
